import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const COLORS = {
  status: {
    OPEN: '#9CA3AF',
    IN_PROGRESS: '#3B82F6',
    REVIEW: '#8B5CF6',
    DONE: '#10B981',
    CLOSED: '#6B7280',
  },
  priority: {
    P1: '#EF4444',
    P2: '#F59E0B',
    P3: '#10B981',
  },
  type: {
    Bugfix: '#EF4444',
    Enhancement: '#3B82F6',
    Feature: '#10B981',
    Support: '#8B5CF6',
    Other: '#6B7280',
  },
};

const STATUS_LABELS: Record<string, string> = {
  OPEN: '대기',
  IN_PROGRESS: '진행중',
  REVIEW: '검토',
  DONE: '완료',
  CLOSED: '종료',
};

const TYPE_LABELS: Record<string, string> = {
  Bugfix: '버그수정',
  Enhancement: '개선',
  Feature: '신규기능',
  Support: '지원',
  Other: '기타',
};

interface StatusChartProps {
  data: Record<string, number>;
}

export function StatusPieChart({ data }: StatusChartProps) {
  const chartData = Object.entries(data).map(([name, value]) => ({
    name: STATUS_LABELS[name] || name,
    value,
    color: COLORS.status[name as keyof typeof COLORS.status] || '#6B7280',
  }));

  if (chartData.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-gray-500">
        데이터 없음
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          paddingAngle={2}
          dataKey="value"
          label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

interface PriorityChartProps {
  data: Record<string, number>;
}

export function PriorityBarChart({ data }: PriorityChartProps) {
  const chartData = Object.entries(data).map(([name, value]) => ({
    name,
    count: value,
    fill: COLORS.priority[name as keyof typeof COLORS.priority] || '#6B7280',
  }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={chartData} layout="vertical">
        <XAxis type="number" />
        <YAxis dataKey="name" type="category" width={40} />
        <Tooltip />
        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

interface TypeChartProps {
  data: Record<string, number>;
}

export function TypeBarChart({ data }: TypeChartProps) {
  const chartData = Object.entries(data).map(([name, value]) => ({
    name: TYPE_LABELS[name] || name,
    count: value,
    fill: COLORS.type[name as keyof typeof COLORS.type] || '#6B7280',
  }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={chartData}>
        <XAxis dataKey="name" />
        <YAxis />
        <Tooltip />
        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

interface MonthlyTrendProps {
  data: Array<{ month: string; hours: number; requests: number }>;
}

export function MonthlyTrendChart({ data }: MonthlyTrendProps) {
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <XAxis dataKey="month" />
        <YAxis yAxisId="left" orientation="left" stroke="#3B82F6" />
        <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
        <Tooltip />
        <Legend />
        <Bar yAxisId="left" dataKey="hours" fill="#3B82F6" name="작업시간(h)" radius={[4, 4, 0, 0]} />
        <Bar yAxisId="right" dataKey="requests" fill="#10B981" name="요청건수" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
